import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Check, X, Plus } from "lucide-react";
import { Skeleton, ErrorState } from "../ui/LoadingStates";
import { toast } from "../ui/Toast";
import { useConfirmDelete } from "../../hooks/useConfirmDelete";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";

// Keys MUST match the backend GoalStatus enum (ON_TRACK | AT_RISK | COMPLETED).
// ON_TRACK is the default for new goals; the enum has no in-progress/not-started
// members, so an unknown value falls back to STATUS_FALLBACK (grey).
const STATUS_STYLE = {
  ON_TRACK:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  AT_RISK:   "text-red-400 bg-red-500/10 border-red-500/20",
  COMPLETED: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";

export default function GoalTracker() {
  const [goals,        setGoals]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [editingId,    setEditingId]    = useState(null);
  const [editDraft,    setEditDraft]    = useState({});
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [newGoal,      setNewGoal]      = useState({ title: "", description: "", due_date: "" });
  const [saving,       setSaving]       = useState(false);
  const { confirmDelete, ConfirmDialog } = useConfirmDelete();

  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/performance/goals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setGoals(Array.isArray(data) ? data : data.goals || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = (id) => {
    confirmDelete("this goal", async () => {
      try {
        const res = await fetch(`${API_BASE}/api/performance/goals/${id}`, {
          method: "DELETE", headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed");
        setGoals(g => g.filter(x => x.id !== id));
        toast.success("Goal deleted");
      } catch (e) { toast.error("Failed to delete goal"); }
    });
  };

  const handleEditSubmit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/goals/${editingId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(editDraft),
      });
      if (!res.ok) throw new Error("Failed");
      setGoals(g => g.map(x => x.id === editingId ? { ...x, ...editDraft } : x));
      setEditingId(null);
      toast.success("Goal updated");
    } catch (e) { toast.error("Failed to update goal"); }
    finally { setSaving(false); }
  };

  const handleAdd = async () => {
    if (!newGoal.title.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/goals`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(newGoal),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      setGoals(g => [...g, created]);
      setNewGoal({ title: "", description: "", due_date: "" });
      setShowAddForm(false);
      toast.success("Goal created");
    } catch (e) { toast.error("Failed to create goal"); }
    finally { setSaving(false); }
  };

  const done  = goals.filter(g => g.status === "COMPLETED").length;
  const total = goals.length;
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Goals</span>
        <div className="flex items-center gap-3">
          {total > 0 && (
            <span className="text-xs text-white/30">{done}/{total} complete</span>
          )}
          <button onClick={() => setShowAddForm(v => !v)}
            className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={11} /> Add
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Progress bar */}
        {total > 0 && (
          <div>
            <div className="h-1.5 rounded-full bg-white/5">
              <div className="h-full rounded-full bg-[#00ff90] transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-white/30 mt-1">{pct}% of goals completed</p>
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[1,2,3].map(i => (
              <div key={i} className="rounded-lg border border-white/5 bg-white/3 p-4"
                   style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Skeleton width="55%" height={12} />
                  <Skeleton width={70} height={18} radius={999} />
                </div>
                <Skeleton width="85%" height={10} />
              </div>
            ))}
          </div>
        )}
        {error && <ErrorState message="Couldn't load goals" detail={error} onRetry={load} />}

        {/* Add form */}
        {showAddForm && (
          <div className="rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 p-4 space-y-3">
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-wider">New Goal</p>
            <input placeholder="Goal title" value={newGoal.title} onChange={e => setNewGoal(d => ({ ...d, title: e.target.value }))} className={inputCls} />
            <textarea placeholder="Description (optional)" value={newGoal.description} onChange={e => setNewGoal(d => ({ ...d, description: e.target.value }))} rows={2} className={`${inputCls} resize-none`} />
            <input type="date" value={newGoal.due_date} onChange={e => setNewGoal(d => ({ ...d, due_date: e.target.value }))} className={inputCls} />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAddForm(false)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
              <button onClick={handleAdd} disabled={saving} className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                {saving ? "Saving…" : "Add Goal"}
              </button>
            </div>
          </div>
        )}

        {/* Goal list */}
        {!loading && !error && goals.length === 0 && !showAddForm && (
          <p className="text-white/20 text-sm italic text-center py-4">No goals set yet.</p>
        )}

        {!loading && goals.map(goal => {
          const statusStyle = STATUS_STYLE[goal.status] || STATUS_FALLBACK;
          const isEditing = editingId === goal.id;
          return (
            <div key={goal.id} className="rounded-lg border border-white/5 bg-white/3 p-4">
              {isEditing ? (
                <div className="space-y-3">
                  <input value={editDraft.title ?? ""} onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))} className={inputCls} />
                  <textarea value={editDraft.description ?? ""} onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))} rows={2} className={`${inputCls} resize-none`} />
                  <input type="date" value={editDraft.due_date ?? ""} onChange={e => setEditDraft(d => ({ ...d, due_date: e.target.value }))} className={inputCls} />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditingId(null)} className="text-white/40 hover:text-white transition-colors"><X size={14} /></button>
                    <button onClick={handleEditSubmit} disabled={saving} className="text-[#00ff90] hover:text-[#00ff90]/70 transition-colors"><Check size={14} /></button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-medium text-white">{goal.title}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${statusStyle}`}>
                        {(goal.status || "not started").replace("_", " ")}
                      </span>
                    </div>
                    {goal.description && <p className="text-xs text-white/40 mt-0.5">{goal.description}</p>}
                    {goal.due_date    && <p className="text-xs text-white/25 mt-1">Due: {goal.due_date}</p>}
                    {goal.progress != null && (
                      <div className="mt-2">
                        <div className="h-1 rounded-full bg-white/5 w-32">
                          <div className="h-full rounded-full bg-[#00ff90]" style={{ width: `${goal.progress}%` }} />
                        </div>
                        <p className="text-xs text-white/20 mt-0.5">{goal.progress}%</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => { setEditingId(goal.id); setEditDraft(goal); }} className="text-white/20 hover:text-blue-400 transition-colors"><Pencil size={13} /></button>
                    <button onClick={() => handleDelete(goal.id)} className="text-white/20 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {ConfirmDialog}
    </div>
  );
}